import { FeatureFlag } from '../types/features';

interface FeatureEvent {
  featureId: string;
  eventType: 'enable' | 'disable' | 'error' | 'usage';
  timestamp: number;
  metadata?: Record<string, any>;
}

class FeatureAnalytics {
  private static instance: FeatureAnalytics;
  private events: FeatureEvent[] = [];
  private readonly STORAGE_KEY = 'feature_analytics';
  
  private constructor() {
    this.loadEvents();
  }

  static getInstance(): FeatureAnalytics {
    if (!FeatureAnalytics.instance) {
      FeatureAnalytics.instance = new FeatureAnalytics();
    }
    return FeatureAnalytics.instance;
  }

  private loadEvents(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.events = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading feature analytics:', error);
    }
  }

  private saveEvents(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.events));
    } catch (error) {
      console.error('Error saving feature analytics:', error);
    }
  }

  trackEvent(featureId: string, eventType: FeatureEvent['eventType'], metadata?: Record<string, any>): void {
    const event: FeatureEvent = {
      featureId,
      eventType,
      timestamp: Date.now(),
      metadata,
    };
    
    this.events.push(event);
    this.saveEvents();
  }

  getFeatureUsage(featureId: string): FeatureEvent[] {
    return this.events.filter(event => event.featureId === featureId);
  }

  getFeatureMetrics(featureId: string) {
    const events = this.getFeatureUsage(featureId);
    const enableCount = events.filter(e => e.eventType === 'enable').length;
    const disableCount = events.filter(e => e.eventType === 'disable').length;
    const errorCount = events.filter(e => e.eventType === 'error').length;
    const usageCount = events.filter(e => e.eventType === 'usage').length;

    return {
      enableCount,
      disableCount,
      errorCount,
      usageCount,
      totalEvents: events.length,
    };
  }

  getGlobalMetrics() {
    const uniqueFeatures = new Set(this.events.map(e => e.featureId));
    return {
      totalEvents: this.events.length,
      uniqueFeatures: uniqueFeatures.size,
      eventsByType: {
        enable: this.events.filter(e => e.eventType === 'enable').length,
        disable: this.events.filter(e => e.eventType === 'disable').length,
        error: this.events.filter(e => e.eventType === 'error').length,
        usage: this.events.filter(e => e.eventType === 'usage').length,
      },
    };
  }

  clearAnalytics(): void {
    this.events = [];
    this.saveEvents();
  }
}

export const featureAnalytics = FeatureAnalytics.getInstance();
